import { closureButtonClass } from "../../utils/closureButtonStyle";
import React, { useState, useEffect, useCallback } from "react";
import {
  itIT,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import { Button } from "../../components/Button";
import { Select } from "../../components/Select";
import { Input } from "../../components/Input";
import Modal from "../../components/Modal";
import api from "../../api/api";
import { EXPORT_BUTTON_CLASS } from "../../utils/exportButtonStyle";
import { toast } from "react-toastify";
import { HiOutlineRefresh, HiOutlineDownload, HiOutlineCheck, HiOutlineTable, HiOutlineCalendar, HiOutlineExclamation, HiOutlinePencil, HiOutlineAnnotation } from "react-icons/hi";
import { MdPlayArrow, MdTrendingUp, MdBarChart } from "react-icons/md";
import { knowAcces } from "../../utils/utils";
import { GridVisibleRowsCount } from "../../components/DataGrid/CustomFooterPersonalized";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const SEZIONI = [
  { id: "all",                  name: "Tutte le sezioni" },
  { id: "payroll",              name: "Payroll BPoint + Zucchetti + iiPay" },
  { id: "payroll_bpoint",       name: "Payroll BPoint" },
  { id: "altri_adempimenti",   name: "Altri adempimenti" },
  { id: "forfait_consulenza",  name: "Forfait consulenza" },
  { id: "forfait_f24",         name: "Forfait F24" },
  { id: "import_file",         name: "Import file" },
  { id: "sconto_cliente",      name: "Sconto cliente" },
];

const SEZIONE_LABEL = {
  ...Object.fromEntries(SEZIONI.slice(1).map(s => [s.id, s.name])),
  payroll_zucchetti: "Payroll Zucchetti",
  payroll_iipay: "Payroll iiPay",
  payroll_bpoint: "Payroll BPoint",
};

const SEZ_COL = {
  payroll_zucchetti:  "chiuso_sez1",
  payroll_iipay:      "chiuso_sez2",
  payroll_bpoint:     "chiuso_sez0",
  altri_adempimenti:  "chiuso_sez3",
  forfait_consulenza: "chiuso_sez4",
  forfait_f24:        "chiuso_sez5",
  import_file:        "chiuso_sez6",
};

const STATO_COLORS = {
  bozza:    "bg-gray-100 text-gray-700",
  validata: "bg-green-100 text-green-700",
  forzata:  "bg-yellow-100 text-yellow-700",
  esclusa:  "bg-red-100 text-red-600 line-through",
  chiusa:   "bg-blue-100 text-blue-700",
};

const ORIGINE_LABEL = {
  regola:           "Auto",
  manuale:          "Manuale",
  import_zucchetti: "Import Zuc",
  import_file:      "Import file",
  forzatura_admin:  "Forzato",
};

const SETTORE_OPTIONS = [
  { id: "PAGHE", name: "Paghe" },
  { id: "ASSUNZIONI", name: "Assunzioni" },
  { id: "CONSULENZA", name: "Consulenza" },
];

const TIPOLOGIA_OPTIONS = [
  { id: "Z", name: "Zucchetti" },
  { id: "I", name: "iiPay" },
  { id: "B", name: "Bpoint" },
];

const TIPOLOGIA_LABEL = {
  Z: "Zucchetti",
  I: "iiPay",
  B: "Bpoint",
};

const NOTE_TYPE_OPTIONS = [
  { id: "SETTORE", name: "Settore" },
  { id: "ELABORATORE_PAGHE", name: "Elaboratore paghe" },
  { id: "ADMIN", name: "Admin" },
];

const NOTE_TYPE_LABEL = {
  SETTORE: "Settore",
  ELABORATORE_PAGHE: "Elaboratore paghe",
  ADMIN: "Admin",
};

const EMPTY_NOTE_FORM = {
  id: null,
  tipo: "SETTORE",
  settore: "PAGHE",
  codice_parcellazione: "",
  titolo: "",
  nota: "",
  alert: false,
};

const fmt = (v) =>
  v != null
    ? formatItalianNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const SHOW_UNIFICATE_KEY = "parcellazione.billingLines.showUnificate";

const joinDescrizioni = (values) => {
  const result = [];
  values.forEach((value) => {
    String(value || "")
      .split(";")
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((v) => {
        if (!result.includes(v)) result.push(v);
      });
  });
  return result.join("; ") || null;
};

const appendCodiceClienteIipay = (descrizione, line) => {
  const codiceCliente = line?.cliente_diretto_iipay && line?.codice_cliente_iipay
    ? String(line.codice_cliente_iipay).trim()
    : "";
  const descrizionePulita = String(descrizione || "").trim();

  if (!codiceCliente) return descrizionePulita;
  if (!descrizionePulita) return codiceCliente;
  if (descrizionePulita.toUpperCase().includes(codiceCliente.toUpperCase())) return descrizionePulita;
  return `${descrizionePulita} ${codiceCliente}`;
};

const composeDescrizioneFattura = (line) => {
  const descrizionePrestazione = String(line?.descrizione_prestazione || "").trim();
  const descrizioneExtra = String(line?.descrizione_extra_fattura || "").trim();
  const appendVoce = descrizionePrestazione &&
    String(line?.descrizione_fattura || "").startsWith(descrizionePrestazione);
  const descrizione = descrizioneExtra && appendVoce
    ? `${descrizionePrestazione} - ${descrizioneExtra}`
    : descrizioneExtra
    ? descrizioneExtra
    : descrizionePrestazione;

  return appendCodiceClienteIipay(descrizione, line);
};

const CODICI_NON_UNIFICABILI = new Set(["1222", "1236"]);

const isRigaUnificabile = (line) =>
  line?.sezione === "altri_adempimenti" &&
  !CODICI_NON_UNIFICABILI.has(
    String(line?.nuovo_codice_ago || line?.prestazione || "").trim().replace(/^0+(?=\d)/, "")
  ) &&
  line?.prestazione != null &&
  line?.quantita != null &&
  line?.importo_unitario != null &&
  !["esclusa", "eliminata", "forzata"].includes(line?.stato);

const getSettoreRiga = (line) => {
  const settore = String(line?.settore || "").trim().toUpperCase();
  if (settore) return settore;
  if (["payroll_bpoint", "payroll_zucchetti", "payroll_iipay"].includes(line?.sezione)) return "PAGHE";
  if (line?.sezione === "forfait_consulenza") return "CONSULENZA";
  return "";
};

const getTipologiaRiga = (line) =>
  String(line?.tipologia_prestazione || line?.tipologia_fatturazione || "").trim().toUpperCase();

const getSelectValue = (event) => event.target.value === "0" ? "" : event.target.value;

const getNoteSummary = (note) => note?.titolo || note?.nota || "";

const groupAltriAdempimentiLines = (lines = []) => {
  const grouped = new Map();
  const output = [];

  lines.forEach((line) => {
    if (!isRigaUnificabile(line)) {
      output.push(line);
      return;
    }

    const key = [
      line.billing_period_id,
      line.codice_univoco_azienda,
      line.prestazione,
      line.importo_unitario,
      line.stato,
    ].join("|");

    if (!grouped.has(key)) {
      const clone = { ...line, id: `unified-${key}`, original_ids: [line.id], is_unified: false };
      grouped.set(key, clone);
      output.push(clone);
      return;
    }

    const current = grouped.get(key);
    current.is_unified = true;
    current.original_ids.push(line.id);
    current.quantita = (Number(current.quantita) || 0) + (Number(line.quantita) || 0);
    current.totale = Number(((Number(current.totale) || 0) + (Number(line.totale) || 0)).toFixed(2));
    current.descrizione_extra_fattura = joinDescrizioni([current.descrizione_extra_fattura, line.descrizione_extra_fattura]);
    current.descrizione_interna = joinDescrizioni([current.descrizione_interna, line.descrizione_interna]);
    current.descrizione_fattura = composeDescrizioneFattura(current);
    current.created_by = joinDescrizioni([current.created_by, line.created_by]);
  });

  return output;
};

export const BillingLines = ({ data }) => {
  const [periodo, setPeriodo]     = useState(null);
  const [linee, setLinee]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [generating, setGenerating] = useState(false);
  const [closing, setClosing]     = useState(false);
  const [sezione, setSezione]     = useState("all");
  const [exporting, setExporting] = useState(false);
  const [showUnificate, setShowUnificate] = useState(() => localStorage.getItem(SHOW_UNIFICATE_KEY) !== "false");

  const [view, setView]                   = useState("righe");
  const [summary, setSummary]             = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [referenti, setReferenti]         = useState([]);
  const [clientiMap, setClientiMap]       = useState({});
  const [referenteFilter, setReferenteFilter] = useState("");
  const [gruppoFilter, setGruppoFilter]       = useState("");
  const [settoreFilter, setSettoreFilter]     = useState("");
  const [tipologiaFilter, setTipologiaFilter] = useState(new Set());
  const [codiceFilter, setCodiceFilter]       = useState("");

  const [pianoOpen, setPianoOpen]         = useState(false);
  const [pianoData, setPianoData]         = useState(null);
  const [pianoAnno, setPianoAnno]         = useState(new Date().getFullYear());
  const [pianoLoading, setPianoLoading]   = useState(false);
  const [exportForfaitLoading, setExportForfaitLoading] = useState(false);
  const [expiringOpen, setExpiringOpen]   = useState(false);
  const [expiringRows, setExpiringRows]   = useState([]);

  const [notes, setNotes]                 = useState({});
  const [noteEditOpen, setNoteEditOpen]   = useState(false);
  const [noteEditCodice, setNoteEditCodice] = useState(null);
  const [noteEditLabel, setNoteEditLabel] = useState("");
  const [noteForm, setNoteForm]           = useState(EMPTY_NOTE_FORM);
  const [noteSaving, setNoteSaving]       = useState(false);
  const [iipayStatusOpen, setIipayStatusOpen] = useState(false);
  const [iipayStatus, setIipayStatus] = useState(null);
  const [iipayPresunti, setIipayPresunti] = useState([]);
  const [iipaySavingPresunti, setIipaySavingPresunti] = useState(false);

  const [trendData, setTrendData]         = useState(null);
  const [trendLoading, setTrendLoading]   = useState(false);
  const [trendMesi, setTrendMesi]         = useState(6);

  const [istatOpen, setIstatOpen]         = useState(false);
  const [istatPerc, setIstatPerc]         = useState("");
  const [istatTipo, setIstatTipo]         = useState("");
  const [istatArrot, setIstatArrot]       = useState("");
  const [istatNote, setIstatNote]         = useState("");
  const [istatApplying, setIstatApplying] = useState(false);
  const [istatLog, setIstatLog]           = useState([]);

  const isAdmin = knowAcces(["parc_admin"]);
  const [anno, mese] = data ? data.split("-").map(Number) : [null, null];

  // Carica conteggio forfait in scadenza nei prossimi 90gg al mount (solo admin)
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/billing/forfait/expiring", { params: { giorni: 90 } })
      .then(r => setExpiringRows(r.data.rows || []))
      .catch(() => {});
  }, [isAdmin]);

  const loadPeriodo = useCallback(async () => {
    if (!anno || !mese) return;
    try {
      const res = await api.get(`/billing/periods/${anno}/${mese}`);
      setPeriodo(res.data);
    } catch {
      toast.error("Errore caricamento periodo");
    }
  }, [anno, mese]);

  const loadReferenti = useCallback(async (periodoId) => {
    if (!periodoId) return;
    try {
      const res = await api.get(`/billing/periods/${periodoId}/referenti`);
      setReferenti(res.data.referenti || []);
      setClientiMap(res.data.clienti_map || {});
    } catch { /* non bloccante */ }
  }, []);

  const loadLinee = useCallback(async (periodoId) => {
    if (!periodoId) return;
    setLoading(true);
    try {
      const params = sezione !== "all" ? { sezione } : {};
      const res = await api.get(`/billing/lines/${periodoId}`, { params });
      setLinee(res.data);
    } catch {
      toast.error("Errore caricamento righe");
    } finally {
      setLoading(false);
    }
  }, [sezione]);

  useEffect(() => {
    loadPeriodo();
  }, [loadPeriodo]);

  useEffect(() => {
    if (periodo?.id) { loadLinee(periodo.id); loadReferenti(periodo.id); }
    else { setLinee([]); setReferenti([]); setClientiMap({}); }
    setSummary(null);
    setReferenteFilter("");
    setGruppoFilter("");
    setSettoreFilter("");
    setTipologiaFilter("");
    setCodiceFilter("");
    setNotes({});
    setTrendData(null);
  }, [periodo, loadLinee, loadReferenti]);

  useEffect(() => {
    localStorage.setItem(SHOW_UNIFICATE_KEY, String(showUnificate));
  }, [showUnificate]);

  const isSezClosed = sezione !== "all" && !!periodo?.[SEZ_COL[sezione]];

  const openIipayStatus = useCallback((details) => {
    setIipayStatus(details);
    setIipayPresunti((details?.missing || []).map((row) => ({
      codice_univoco_azienda: row.codice_univoco,
      ragione_sociale: row.ragione_sociale,
      codice_parcellazione: row.codice_parcellazione,
      cedolini_elaborati: "",
    })));
    setIipayStatusOpen(true);
  }, []);

  const handleIipayApiError = useCallback((err, fallback) => {
    const details = err.response?.data?.details;
    if (err.response?.status === 409 && details?.missing) {
      openIipayStatus(details);
      toast.error(err.response?.data?.message || "Cedolini iiPay del mese precedente mancanti");
      return;
    }
    toast.error(fallback);
  }, [openIipayStatus]);

  const handleVerificaIipay = async () => {
    if (!periodo?.id) return;
    try {
      const res = await api.get(`/billing/periods/${periodo.id}/iipay-previous-month-status`);
      openIipayStatus(res.data);
      if ((res.data.missing || []).length === 0) toast.success("Cedolini iiPay del mese precedente presenti");
    } catch (err) {
      handleIipayApiError(err, "Errore verifica iiPay");
    }
  };

  const handleSavePresuntiIipay = async () => {
    if (!periodo?.id) return;
    const rows = iipayPresunti
      .filter((row) => row.cedolini_elaborati !== "" && row.cedolini_elaborati != null)
      .map((row) => ({
        codice_univoco_azienda: row.codice_univoco_azienda,
        cedolini_elaborati: Number(row.cedolini_elaborati),
      }));
    if (!rows.length) {
      toast.warning("Inserisci almeno un valore presunto");
      return;
    }
    setIipaySavingPresunti(true);
    try {
      const res = await api.post(`/billing/periods/${periodo.id}/iipay-previous-month-presunti`, { rows });
      openIipayStatus(res.data.status);
      toast.success(`Cedolini presunti salvati: ${res.data.updated}`);
      await loadLinee(periodo.id);
    } catch (err) {
      handleIipayApiError(err, "Errore salvataggio cedolini presunti");
    } finally {
      setIipaySavingPresunti(false);
    }
  };

  const handleChiudiSezione = async () => {
    if (!periodo?.id || sezione === "all") return;
    if (!window.confirm(`Chiudere la sezione "${SEZIONE_LABEL[sezione]}"? Le righe verranno marcate come chiuse e non potranno più essere modificate.`)) return;
    setClosing(true);
    try {
      await api.post(`/billing/periods/${periodo.id}/close`, { sezione });
      toast.success(`Sezione "${SEZIONE_LABEL[sezione]}" chiusa`);
      await loadPeriodo();
      await loadLinee(periodo.id);
    } catch (err) {
      handleIipayApiError(err, "Errore chiusura sezione");
    } finally {
      setClosing(false);
    }
  };

  const handleGenera = async () => {
    if (!periodo?.id) return;
    setGenerating(true);
    try {
      const res = await api.post(`/billing/periods/${periodo.id}/generate`, { sezione: "all" });
      toast.success(`Generate ${res.data.righe_generate} righe`);
      await loadLinee(periodo.id);
    } catch (err) {
      handleIipayApiError(err, "Errore generazione righe");
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenIstat = async () => {
    try {
      const res = await api.get("/billing/forfait/istat");
      setIstatLog(res.data);
    } catch { /* log non bloccante */ }
    setIstatOpen(true);
  };

  const handleApplyIstat = async () => {
    if (!istatPerc || isNaN(istatPerc)) { toast.warning("Inserisci una percentuale valida"); return; }
    if (!window.confirm(`Applicare +${istatPerc}% a tutti i forfait${istatTipo ? ` (${istatTipo})` : ""}? L'operazione è irreversibile.`)) return;
    setIstatApplying(true);
    try {
      const res = await api.post("/billing/forfait/istat", {
        percentuale: parseFloat(istatPerc),
        tipo_filter: istatTipo || null,
        arrotondamento: istatArrot ? parseFloat(istatArrot) : null,
        note: istatNote || null,
      });
      toast.success(`ISTAT applicata: ${res.data.righe_aggiornate} forfait aggiornati`);
      setIstatPerc(""); setIstatTipo(""); setIstatArrot(""); setIstatNote("");
      const log = await api.get("/billing/forfait/istat");
      setIstatLog(log.data);
    } catch {
      toast.error("Errore applicazione ISTAT");
    } finally {
      setIstatApplying(false);
    }
  };

  const handleOpenExpiring = async () => {
    try {
      const res = await api.get("/billing/forfait/expiring", { params: { giorni: 90 } });
      setExpiringRows(res.data.rows || []);
    } catch { setExpiringRows([]); }
    setExpiringOpen(true);
  };

  const handleOpenPiano = async (annoSel) => {
    const a = annoSel || pianoAnno;
    setPianoLoading(true);
    setPianoOpen(true);
    try {
      const res = await api.get(`/billing/forfait/piano`, { params: { anno: a } });
      setPianoData(res.data);
    } catch {
      toast.error("Errore caricamento piano forfait");
    } finally {
      setPianoLoading(false);
    }
  };

  const handleExportForfait = async () => {
    setExportForfaitLoading(true);
    try {
      const res = await api.get("/billing/forfait/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "forfait_attivi.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export forfait");
    } finally {
      setExportForfaitLoading(false);
    }
  };

  const loadNotes = useCallback(async (periodoId) => {
    if (!periodoId) return;
    try {
      const res = await api.get(`/billing/periods/${periodoId}/notes`);
      const map = {};
      for (const n of res.data) {
        if (!map[n.codice_univoco_azienda]) map[n.codice_univoco_azienda] = [];
        map[n.codice_univoco_azienda].push({
          ...n,
          alert: Number(n.alert) === 1,
        });
      }
      setNotes(map);
    } catch { /* non bloccante */ }
  }, []);

  const handleLoadTrend = useCallback(async (periodoId, mesi) => {
    if (!periodoId) return;
    setTrendLoading(true);
    try {
      const res = await api.get(`/billing/periods/${periodoId}/trend`, { params: { mesi: mesi || trendMesi } });
      setTrendData(res.data);
    } catch {
      toast.error("Errore caricamento trend");
    } finally {
      setTrendLoading(false);
    }
  }, [trendMesi]);

  useEffect(() => {
    if (periodo?.id) loadNotes(periodo.id);
  }, [periodo?.id, loadNotes]);

  const handleSaveNote = async () => {
    if (!noteEditCodice || !periodo?.id) return;
    setNoteSaving(true);
    try {
      await api.put(`/billing/periods/${periodo.id}/notes/${noteEditCodice}`, {
        ...noteForm,
        alert: noteForm.alert ? 1 : 0,
        settore: noteForm.tipo === "SETTORE" ? noteForm.settore : null,
      });
      await loadNotes(periodo.id);
      setNoteEditOpen(false);
    } catch {
      toast.error("Errore salvataggio nota");
    } finally {
      setNoteSaving(false);
    }
  };

  const handleLoadSummary = useCallback(async (periodoId) => {
    if (!periodoId) return;
    setSummaryLoading(true);
    try {
      const res = await api.get(`/billing/lines/${periodoId}/summary`);
      setSummary(res.data);
    } catch {
      toast.error("Errore caricamento riepilogo");
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "riepilogo" && periodo?.id) {
      if (!summary) handleLoadSummary(periodo.id);
      loadNotes(periodo.id);
    }
    if (view === "trend" && periodo?.id && !trendData) {
      handleLoadTrend(periodo.id, trendMesi);
    }
  }, [view, periodo, summary, trendData, trendMesi, handleLoadSummary, loadNotes, handleLoadTrend]);

  const handleExport = async () => {
    if (!periodo?.id) return;
    setExporting(true);
    try {
      const params = sezione !== "all" ? { sezione } : {};
      const res = await api.get(`/billing/lines/${periodo.id}/export`, {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const meseStr = String(mese).padStart(2, "0");
      a.download = `righe_fattura_${anno}_${meseStr}${sezione !== "all" ? "_" + sezione : ""}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export Excel");
    } finally {
      setExporting(false);
    }
  };

  // Valori distinti per i dropdown filtro (calcolati dalle righe caricate)
  const gruppiDisponibili = [...new Set(linee.map(l => l.gruppo_cliente).filter(Boolean))].sort();
  const settoriDisponibili = SETTORE_OPTIONS.filter((s) => linee.some((l) => getSettoreRiga(l) === s.id));
  const tipologieDisponibili = TIPOLOGIA_OPTIONS.filter((t) => linee.some((l) => getTipologiaRiga(l) === t.id));

  const lineeFiltrateBase = linee.filter(l => {
    if (referenteFilter && clientiMap[l.codice_univoco_azienda] !== referenteFilter) return false;
    if (gruppoFilter    && l.gruppo_cliente !== gruppoFilter) return false;
    if (settoreFilter   && getSettoreRiga(l) !== settoreFilter) return false;
    if (tipologiaFilter.size > 0 && !tipologiaFilter.has(getTipologiaRiga(l))) return false;
    if (codiceFilter && !String(l.prestazione || "").includes(String(codiceFilter).trim())) return false;
    return true;
  });
  const lineeFiltrate = showUnificate ? groupAltriAdempimentiLines(lineeFiltrateBase) : lineeFiltrateBase;

  const totale = lineeFiltrateBase
    .filter(l => l.stato !== "esclusa")
    .reduce((s, l) => s + (Number(l.totale) || 0), 0);

  const getNotesCliente = useCallback((codice) => notes[codice] || [], [notes]);
  const hasAlertCliente = useCallback((codice) => getNotesCliente(codice).some((n) => n.alert), [getNotesCliente]);

  const openNoteEditor = useCallback((codice, label, note = null) => {
    setNoteEditCodice(codice);
    setNoteEditLabel(label);
    setNoteForm(note ? {
      id: note.id,
      tipo: note.tipo || "ADMIN",
      settore: note.settore || "PAGHE",
      codice_parcellazione: note.codice_parcellazione || "",
      titolo: note.titolo || "",
      nota: note.nota || "",
      alert: Number(note.alert) === 1 || note.alert === true,
    } : EMPTY_NOTE_FORM);
    setNoteEditOpen(true);
  }, []);

  const columns = [
    {
      field: "ragione_sociale",
      headerName: "Cliente",
      flex: 2,
      minWidth: 160,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "note_cliente",
      headerName: "Note",
      width: 90,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const rowNotes = getNotesCliente(params.row.codice_univoco_azienda);
        if (!rowNotes.length) return <span className="text-gray-300">—</span>;
        const hasAlert = rowNotes.some((n) => n.alert);
        return (
          <div className="flex items-center justify-center gap-1" title={rowNotes.map(getNoteSummary).join("\n")}>
            {hasAlert && (
              <span className="bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded">!</span>
            )}
            <span className="bg-blue-100 text-blue-700 text-xs font-medium px-1.5 py-0.5 rounded">
              {rowNotes.length}
            </span>
          </div>
        );
      },
    },
    {
      field: "sezione",
      headerName: "Sezione",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => SEZIONE_LABEL[params.value] || params.value,
    },
    {
      field: "settore_operativo",
      headerName: "Settore",
      width: 115,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => {
        const settore = getSettoreRiga(params.row);
        return SETTORE_OPTIONS.find((s) => s.id === settore)?.name || settore || "—";
      },
    },
    {
      field: "tipologia_visualizzazione",
      headerName: "Tipo",
      width: 95,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => TIPOLOGIA_LABEL[getTipologiaRiga(params.row)] || getTipologiaRiga(params.row) || "—",
    },
    {
      field: "prestazione",
      headerName: "Cod.",
      width: 70,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "descrizione_fattura",
      headerName: "Descrizione",
      flex: 2,
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => params.row.descrizione_fattura || params.row.descrizione_prestazione || params.row.descrizione_extra_fattura,
    },
    {
      field: "quantita",
      headerName: "Qtà",
      width: 70,
      headerAlign: "right",
      align: "right",
      valueFormatter: (params) => params.value != null ? params.value : "—",
    },
    {
      field: "importo_unitario",
      headerName: "Imp. unit.",
      width: 95,
      headerAlign: "right",
      align: "right",
      valueFormatter: (params) => fmt(params.value),
    },
    {
      field: "totale",
      headerName: "Totale €",
      width: 105,
      headerAlign: "right",
      align: "right",
      valueFormatter: (params) => fmt(params.value),
    },
    {
      field: "origine",
      headerName: "Origine",
      width: 90,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => ORIGINE_LABEL[params.value] || params.value,
    },
    {
      field: "stato",
      headerName: "Stato",
      width: 90,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATO_COLORS[params.value] || ""}`}>
            {params.value}
          </span>
          {params.row.is_unified && (
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
              unificata
            </span>
          )}
        </div>
      ),
    },
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <div className="w-full flex flex-wrap-reverse md:flex-nowrap justify-between md:space-x-5 mb-2 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <GridToolbarQuickFilter placeholder="Cerca..." />
          <div className="w-52">
            <Select
              name="sezione_filter"
              value={sezione}
              onChange={(e) => setSezione(e.target.value)}
              options={SEZIONI}
              label=""
            />
          </div>
          {referenti.length > 0 && (
            <div className="w-44">
              <Select
                name="referente_filter"
                value={referenteFilter}
                onChange={(e) => setReferenteFilter(getSelectValue(e))}
                options={referenti.map(r => ({ id: r.codice_zucchetti, name: r.nome }))}
                placeholder="Tutti i referenti"
                label=""
              />
            </div>
          )}
          {gruppiDisponibili.length > 0 && (
            <div className="w-40">
              <Select
                name="gruppo_filter"
                value={gruppoFilter}
                onChange={(e) => setGruppoFilter(getSelectValue(e))}
                options={gruppiDisponibili.map(g => ({ id: g, name: g }))}
                placeholder="Tutti i gruppi"
                label=""
              />
            </div>
          )}
          {settoriDisponibili.length > 1 && (
            <div className="w-40">
              <Select
                name="settore_filter"
                value={settoreFilter}
                onChange={(e) => setSettoreFilter(getSelectValue(e))}
                options={settoriDisponibili}
                placeholder="Tutti i settori"
                label=""
              />
            </div>
          )}
          {tipologieDisponibili.length > 1 && (
            <div className="flex items-center gap-2">
              {[
                ["Z", "bg-blue-100 text-blue-700", "border-blue-400 ring-blue-300"],
                ["I", "bg-green-100 text-green-700", "border-green-400 ring-green-300"],
                ["B", "bg-purple-100 text-purple-700", "border-purple-400 ring-purple-300"],
              ].filter(([id]) => tipologieDisponibili.some((tipo) => tipo.id === id)).map(([id, colorClass, activeClass]) => (
                <button key={id} type="button" onClick={() => setTipologiaFilter((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
                  className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologiaFilter.has(id) ? `${colorClass} ${activeClass} ring-1` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>
                  {id}
                </button>
              ))}
            </div>
          )}
          <div className="w-28">
            <Input
              label=""
              type="text"
              value={codiceFilter}
              onChange={(e) => setCodiceFilter(e.target.value)}
              placeholder="Codice"
              showClear
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Periodo: <strong>{periodo ? `${String(mese).padStart(2,"0")}/${anno}` : "—"}</strong>
            {periodo && (
              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                periodo.stato === "chiuso" ? "bg-blue-100 text-blue-700"
                  : periodo.stato === "chiuso_parzialmente" ? "bg-yellow-100 text-yellow-700"
                  : "bg-green-100 text-green-700"
              }`}>
                {periodo.stato?.replace("_", " ")}
              </span>
            )}
          </span>
          {isAdmin && (
            <Button
              className="principal-button"
              text={generating ? "Generazione..." : "Genera righe"}
              type="button"
              icon={<MdPlayArrow className="button-icon" />}
              onClick={handleGenera}
              disabled={generating || !periodo}
            />
          )}
          {isAdmin && sezione === "payroll" && (
            <Button
              className="edit-button"
              text="Verifica iiPay"
              type="button"
              icon={<HiOutlineExclamation className="button-icon" />}
              onClick={handleVerificaIipay}
              disabled={!periodo}
            />
          )}
          {isAdmin && sezione === "altri_adempimenti" && (
            <button className={closureButtonClass(isSezClosed)} type="button" onClick={handleChiudiSezione}
              disabled={closing || !periodo || isSezClosed}>
              {closing ? "Attendi..." : isSezClosed ? "Sezione chiusa" : "Chiudi sezione"}
            </button>
          )}
          <Button
            className={EXPORT_BUTTON_CLASS}
            text={exporting ? "Export..." : "Excel"}
            type="button"
            icon={<HiOutlineDownload className="button-icon" />}
            onClick={handleExport}
            disabled={exporting || !periodo || !linee.length}
          />
          {isAdmin && (
            <Button
              className="edit-button"
              text="ISTAT"
              type="button"
              icon={<MdTrendingUp className="button-icon" />}
              onClick={handleOpenIstat}
            />
          )}
          {isAdmin && (
            <Button
              className="edit-button"
              text={pianoLoading ? "..." : "Piano"}
              type="button"
              icon={<HiOutlineCalendar className="button-icon" />}
              onClick={() => handleOpenPiano()}
              disabled={pianoLoading}
            />
          )}
          {isAdmin && (
            <Button
              className={EXPORT_BUTTON_CLASS}
              text={exportForfaitLoading ? "Export..." : "Exp. forfait"}
              type="button"
              icon={<HiOutlineDownload className="button-icon" />}
              onClick={handleExportForfait}
              disabled={exportForfaitLoading}
            />
          )}
          {isAdmin && (
            <div className="relative inline-block">
              <Button
                className="edit-button"
                text="Scadenze"
                type="button"
                icon={<HiOutlineExclamation className="button-icon" />}
                onClick={handleOpenExpiring}
              />
              {expiringRows.length > 0 && (
                <span className="pointer-events-none absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-0.5 flex items-center justify-center">
                  {expiringRows.length > 9 ? "9+" : expiringRows.length}
                </span>
              )}
            </div>
          )}
          {isAdmin && (
            <Button
              className={view === "riepilogo" ? "principal-button" : "edit-button"}
              text="Riepilogo"
              type="button"
              icon={<HiOutlineTable className="button-icon" />}
              onClick={() => {
                const next = view === "riepilogo" ? "righe" : "riepilogo";
                setView(next);
                if (next === "riepilogo") setSummary(null);
              }}
            />
          )}
          {isAdmin && (
            <Button
              className={view === "trend" ? "principal-button" : "edit-button"}
              text="Trend"
              type="button"
              icon={<MdBarChart className="button-icon" />}
              onClick={() => {
                const next = view === "trend" ? "righe" : "trend";
                setView(next);
                if (next === "trend") setTrendData(null);
              }}
            />
          )}
          {view === "righe" && (
            <button
              onClick={() => setShowUnificate(v => !v)}
              className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${showUnificate ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}
            >
              {showUnificate ? "Vista unificata" : "Vista singola"}
            </button>
          )}
          <Button
            className="edit-button"
            type="button"
            icon={<HiOutlineRefresh className="button-icon" />}
            onClick={() => {
              if (view === "riepilogo") { setSummary(null); handleLoadSummary(periodo?.id); }
              else if (view === "trend") { setTrendData(null); handleLoadTrend(periodo?.id, trendMesi); }
              else loadLinee(periodo?.id);
            }}
            disabled={!periodo}
          />
        </div>
      </div>
    </GridToolbarContainer>
  );

  const CustomFooter = () => (
    <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50">
      <GridVisibleRowsCount className="text-sm text-gray-500" />
      <span className="text-sm font-semibold">
        Totale: <span className="text-green-700">{fmt(totale)} €</span>
      </span>
    </div>
  );

  return (
    <div className="w-full h-[calc(100vh-140px)] min-h-[620px] relative el-container">
      <Modal
        isOpen={iipayStatusOpen}
        closeButton={() => setIipayStatusOpen(false)}
        headerText="Verifica cedolini iiPay mese precedente"
        size="large"
        leftButtons={
          (iipayStatus?.missing || []).length > 0 ? (
            <Button
              className="principal-button"
              text={iipaySavingPresunti ? "Salvataggio..." : "Salva presunti"}
              type="button"
              icon={<HiOutlineCheck className="button-icon" />}
              onClick={handleSavePresuntiIipay}
              disabled={iipaySavingPresunti}
            />
          ) : null
        }
      >
        <div className="flex flex-col gap-3 p-2">
          <div className="text-sm text-gray-600">
            Mese precedente:{" "}
            <strong>
              {String(iipayStatus?.mese_precedente?.mese || "").padStart(2, "0")}/{iipayStatus?.mese_precedente?.anno || ""}
            </strong>
          </div>
          {(iipayStatus?.missing || []).length === 0 ? (
            <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              Tutti i clienti iiPay hanno i cedolini del mese precedente.
            </div>
          ) : (
            <>
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                Mancano i cedolini del mese precedente per questi clienti. Per il primo mese inserisci il numero presunto: verra sostituito dal dato reale quando il mese sara importato o aggiornato.
              </div>
              <div className="max-h-[45vh] overflow-auto border border-gray-200 rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">Cliente</th>
                      <th className="text-left px-2 py-1 w-32">Cod.</th>
                      <th className="text-right px-2 py-1 w-36">Cedolini presunti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iipayPresunti.map((row, idx) => (
                      <tr key={row.codice_univoco_azienda} className="border-t border-gray-100">
                        <td className="px-2 py-1">{row.ragione_sociale}</td>
                        <td className="px-2 py-1 text-gray-500">{row.codice_parcellazione}</td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            value={row.cedolini_elaborati}
                            onChange={(e) => setIipayPresunti((prev) => prev.map((item, i) =>
                              i === idx ? { ...item, cedolini_elaborati: e.target.value } : item
                            ))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={istatOpen}
        closeButton={() => setIstatOpen(false)}
        headerText="Variazione ISTAT forfait"
        size="medium"
        leftButtons={
          <Button
            className="principal-button"
            text={istatApplying ? "Applicazione..." : "Applica"}
            type="button"
            icon={<HiOutlineCheck className="button-icon" />}
            onClick={handleApplyIstat}
            disabled={istatApplying}
          />
        }
      >
        <div className="flex flex-col gap-3 p-1">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Percentuale aumento %"
              type="number"
              placeholder="es. 2.3"
              value={istatPerc}
              onChange={(e) => setIstatPerc(e.target.value)}
            />
            <Select
              label="Tipo forfait (vuoto = tutti)"
              value={istatTipo}
              onChange={(e) => setIstatTipo(e.target.value)}
              options={[
                { id: "",           name: "Tutti" },
                { id: "consulenza", name: "Consulenza" },
                { id: "f24",        name: "F24" },
              ]}
            />
            <Select
              label="Arrotondamento"
              value={istatArrot}
              onChange={(e) => setIstatArrot(e.target.value)}
              options={[
                { id: "",    name: "Nessuno (2 decimali)" },
                { id: "0.5", name: "0,50 €" },
                { id: "1",   name: "1,00 €" },
                { id: "5",   name: "5,00 €" },
              ]}
            />
            <Input
              label="Note (opzionale)"
              type="text"
              value={istatNote}
              onChange={(e) => setIstatNote(e.target.value)}
            />
          </div>
          {istatLog.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Storico ultime variazioni</p>
              <table className="w-full text-xs border rounded">
                <thead>
                  <tr className="bg-gray-100 text-gray-600">
                    <th className="px-2 py-1 text-left">Data</th>
                    <th className="px-2 py-1 text-right">%</th>
                    <th className="px-2 py-1 text-center">Tipo</th>
                    <th className="px-2 py-1 text-right">Forfait</th>
                    <th className="px-2 py-1 text-left">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {istatLog.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td className="px-2 py-1">{new Date(l.data_applicazione).toLocaleDateString("it-IT")}</td>
                      <td className="px-2 py-1 text-right font-medium">+{l.percentuale}%</td>
                      <td className="px-2 py-1 text-center">{l.tipo_filter || "tutti"}</td>
                      <td className="px-2 py-1 text-right">{l.righe_aggiornate}</td>
                      <td className="px-2 py-1 text-gray-500">{l.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={expiringOpen}
        closeButton={() => setExpiringOpen(false)}
        headerText="Forfait in scadenza (prossimi 90 giorni)"
        size="medium"
      >
        {expiringRows.length === 0 ? (
          <p className="text-gray-500 text-sm p-2">Nessun forfait in scadenza nei prossimi 90 giorni.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 border-b font-semibold text-gray-700">Cliente</th>
                <th className="text-center px-3 py-2 border-b font-semibold text-gray-700">Tipo</th>
                <th className="text-right px-3 py-2 border-b font-semibold text-gray-700">Importo €</th>
                <th className="text-center px-3 py-2 border-b font-semibold text-gray-700">Scadenza</th>
              </tr>
            </thead>
            <tbody>
              {expiringRows.map((r, i) => {
                const scad = new Date(r.data_fine_incarico);
                const oggi = new Date();
                const gg   = Math.round((scad - oggi) / 86400000);
                return (
                  <tr key={r.id} className={`border-b ${i % 2 === 0 ? "bg-white" : "bg-gray-50"} ${gg <= 7 ? "bg-red-50" : gg <= 30 ? "bg-orange-50" : ""}`}>
                    <td className="px-3 py-1.5 font-medium text-gray-800">{r.ragione_sociale}</td>
                    <td className="px-3 py-1.5 text-center capitalize text-gray-600 text-xs">{r.tipo}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-800">{fmt(r.importo)}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        gg <= 7 ? "bg-red-100 text-red-700" :
                        gg <= 30 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>
                        {scad.toLocaleDateString("it-IT")}
                        <span className="ml-1 text-gray-500">({gg}gg)</span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Modal>

      <Modal
        isOpen={pianoOpen}
        closeButton={() => setPianoOpen(false)}
        headerText={`Piano forfait ${pianoData?.anno || pianoAnno}`}
        size="medium"
        leftButtons={
          <div className="flex items-center gap-2">
            <Button
              className="edit-button"
              text={String(pianoAnno - 1)}
              type="button"
              onClick={() => { setPianoAnno(pianoAnno - 1); handleOpenPiano(pianoAnno - 1); }}
            />
            <Button
              className="edit-button"
              text={String(pianoAnno + 1)}
              type="button"
              onClick={() => { setPianoAnno(pianoAnno + 1); handleOpenPiano(pianoAnno + 1); }}
            />
          </div>
        }
      >
        {pianoLoading ? (
          <LinearProgress />
        ) : pianoData ? (
          <PianoForfaitTable piano={pianoData.piano} fmt={fmt} />
        ) : null}
      </Modal>

      {/* Modal nota cliente */}
      {noteEditOpen && (
        <Modal
          isOpen={noteEditOpen}
          closeButton={() => setNoteEditOpen(false)}
          headerText={`Nota — ${noteEditLabel}`}
          size="medium"
          leftButtons={
            <>
              <Button text="Annulla" className="secondary-button" onClick={() => setNoteEditOpen(false)} />
              <Button
                text={noteSaving ? "Salvataggio..." : noteForm.id && !noteForm.nota ? "Elimina nota" : "Salva"}
                className={noteForm.id && !noteForm.nota ? "delete-button" : "success-button"}
                onClick={handleSaveNote}
                disabled={noteSaving}
              />
            </>
          }
        >
          <div className="flex flex-col gap-3 p-1 min-w-[320px]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select
                label="Tipo nota"
                value={noteForm.tipo}
                onChange={(e) => setNoteForm((prev) => ({ ...prev, tipo: e.target.value }))}
                options={NOTE_TYPE_OPTIONS}
              />
              {noteForm.tipo === "SETTORE" ? (
                <Select
                  label="Settore"
                  value={noteForm.settore}
                  onChange={(e) => setNoteForm((prev) => ({ ...prev, settore: e.target.value }))}
                  options={SETTORE_OPTIONS}
                />
              ) : (
                <Input
                  label="Codice parcellazione"
                  value={noteForm.codice_parcellazione}
                  onChange={(e) => setNoteForm((prev) => ({ ...prev, codice_parcellazione: e.target.value }))}
                  placeholder="Opzionale"
                  showClear
                />
              )}
              {noteForm.tipo === "SETTORE" && (
                <Input
                  label="Codice parcellazione"
                  value={noteForm.codice_parcellazione}
                  onChange={(e) => setNoteForm((prev) => ({ ...prev, codice_parcellazione: e.target.value }))}
                  placeholder="Opzionale"
                  showClear
                />
              )}
              <Input
                label="Titolo"
                value={noteForm.titolo}
                onChange={(e) => setNoteForm((prev) => ({ ...prev, titolo: e.target.value }))}
                placeholder="Opzionale"
                showClear
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={noteForm.alert}
                onChange={(e) => setNoteForm((prev) => ({ ...prev, alert: e.target.checked }))}
              />
              Mostra alert su questa nota
            </label>
            <textarea
              className="w-full border border-gray-300 rounded p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
              rows={5}
              placeholder="Nota interna..."
              value={noteForm.nota}
              onChange={e => setNoteForm((prev) => ({ ...prev, nota: e.target.value }))}
            />
          </div>
        </Modal>
      )}

      {view === "righe" ? (
        <DataGridPro
          density="compact"
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: LinearProgress,
            footer: CustomFooter,
          }}
          loading={loading}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          columns={columns}
          rows={lineeFiltrate}
          getRowId={(r) => r.id}
          getRowClassName={(params) => {
            const classes = [];
            if (params.row.stato === "esclusa") classes.push("opacity-40");
            if (hasAlertCliente(params.row.codice_univoco_azienda)) classes.push("bg-red-50");
            return classes.join(" ");
          }}
        />
      ) : view === "trend" ? (
        <div className="flex flex-col h-full overflow-hidden">
          <CustomToolbar />
          <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs text-gray-500 font-medium">Mostra ultimi:</span>
            {[3, 6, 12].map(n => (
              <button
                key={n}
                className={`text-xs px-2 py-1 rounded font-medium transition-colors ${trendMesi === n ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"}`}
                onClick={() => { setTrendMesi(n); setTrendData(null); handleLoadTrend(periodo?.id, n); }}
              >
                {n} mesi
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-auto">
            {trendLoading ? <LinearProgress /> : trendData ? (
              <TrendTable data={trendData} fmt={fmt} />
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-full overflow-hidden">
          <CustomToolbar />
          <div className="flex-1 overflow-auto">
            {summaryLoading ? (
              <LinearProgress />
            ) : summary ? (() => {
              const clientiAttivi = referenteFilter
                ? new Set(Object.entries(clientiMap).filter(([, v]) => v === referenteFilter).map(([k]) => k))
                : null;
              let filtRows = summary.rows;
              if (clientiAttivi) filtRows = filtRows.filter(r => clientiAttivi.has(r.codice_univoco_azienda));
              if (gruppoFilter)  filtRows = filtRows.filter(r => r.gruppo_cliente === gruppoFilter);
              const filtSummary = filtRows === summary.rows ? summary : { ...summary, rows: filtRows };
              return (
                <>
                  <SummaryTable
	                    summary={filtSummary}
	                    fmt={fmt}
	                    notes={notes}
	                    canEditNotes={isAdmin}
	                    onEditNote={openNoteEditor}
	                  />
                  {summary.per_codice?.length > 0 && (
                    <div className="mt-6">
                      <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 bg-gray-50">
                        Riepilogo per codice prestazione
                      </p>
                      <PerCodiceTable rows={summary.per_codice} fmt={fmt} sezioneLabel={SEZIONE_LABEL} />
                    </div>
                  )}
                </>
              );
            })() : null}
          </div>
        </div>
      )}
    </div>
  );
};

const PerCodiceTable = ({ rows, fmt, sezioneLabel }) => (
  <table className="w-full text-sm border-collapse">
    <thead className="bg-gray-50">
      <tr>
        <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700 text-xs">Sezione</th>
        <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700 text-xs">Cod.</th>
        <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">Descrizione</th>
        <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-700 text-xs">Clienti</th>
        <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">Totale €</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={`${r.sezione}-${r.prestazione}-${i}`} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <td className="px-3 py-1 text-gray-500 text-xs whitespace-nowrap">{sezioneLabel[r.sezione] || r.sezione}</td>
          <td className="px-3 py-1 text-gray-600 font-mono text-xs">{r.prestazione || '—'}</td>
          <td className="px-3 py-1 text-gray-800">{r.descrizione || '—'}</td>
          <td className="px-3 py-1 text-right text-gray-600 tabular-nums">{r.n_clienti}</td>
          <td className="px-3 py-1 text-right font-semibold text-gray-900 tabular-nums">{fmt(r.totale)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MESI_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const PianoForfaitTable = ({ piano, fmt }) => {
  const totaleAnno = piano.reduce((s, p) => s + p.totale, 0);
  const nil = <span className="text-gray-300">—</span>;
  return (
    <table className="w-full text-sm border-collapse">
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700" rowSpan={2}>Mese</th>
          <th className="text-center px-3 py-1 border-b border-blue-200 font-semibold text-blue-700 text-xs" colSpan={3}>Consulenza</th>
          <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-purple-700" rowSpan={2}>F24</th>
          <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-teal-700" rowSpan={2}>Bpoint</th>
          <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-800" rowSpan={2}>Totale €</th>
          <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-500 text-xs" rowSpan={2}>Righe</th>
        </tr>
        <tr>
          <th className="text-right px-3 py-1 border-b border-blue-100 font-medium text-blue-600 text-xs">Zucchetti</th>
          <th className="text-right px-3 py-1 border-b border-blue-100 font-medium text-indigo-600 text-xs">iiPay</th>
          <th className="text-right px-3 py-1 border-b border-blue-100 font-medium text-blue-800 text-xs">Tot.</th>
        </tr>
      </thead>
      <tbody>
        {piano.map((p, i) => (
          <tr key={p.mese} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <td className="px-3 py-1.5 text-gray-700 font-medium">{MESI_IT[p.mese - 1]}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-blue-600 text-xs">{(p.consulenza_zucchetti||0) > 0 ? fmt(p.consulenza_zucchetti) : nil}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-indigo-600 text-xs">{(p.consulenza_iipay||0) > 0 ? fmt(p.consulenza_iipay) : nil}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-blue-800 font-medium">{p.consulenza > 0 ? fmt(p.consulenza) : nil}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-purple-700">{p.f24 > 0 ? fmt(p.f24) : nil}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-teal-700">{p.bpoint > 0 ? fmt(p.bpoint) : nil}</td>
            <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{p.totale > 0 ? fmt(p.totale) : nil}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-gray-400 text-xs">{p.n_righe || ''}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
          <td className="px-3 py-2 text-gray-800">TOTALE ANNO</td>
          <td className="px-3 py-2 text-right text-blue-600 tabular-nums text-xs">{fmt(piano.reduce((s,p)=>s+(p.consulenza_zucchetti||0),0))}</td>
          <td className="px-3 py-2 text-right text-indigo-600 tabular-nums text-xs">{fmt(piano.reduce((s,p)=>s+(p.consulenza_iipay||0),0))}</td>
          <td className="px-3 py-2 text-right text-blue-800 tabular-nums">{fmt(piano.reduce((s,p)=>s+p.consulenza,0))}</td>
          <td className="px-3 py-2 text-right text-purple-700 tabular-nums">{fmt(piano.reduce((s,p)=>s+p.f24,0))}</td>
          <td className="px-3 py-2 text-right text-teal-700 tabular-nums">{fmt(piano.reduce((s,p)=>s+p.bpoint,0))}</td>
          <td className="px-3 py-2 text-right text-green-700 tabular-nums">{fmt(Math.round(totaleAnno*100)/100)}</td>
          <td className="px-3 py-2 text-right tabular-nums text-gray-400">{piano.reduce((s,p)=>s+p.n_righe,0)}</td>
        </tr>
      </tfoot>
    </table>
  );
};

const DeltaBadge = ({ delta }) => {
  if (delta == null) return null;
  const pos = delta > 0;
  const zero = delta === 0;
  return (
    <span className={`text-xs font-medium px-1 py-0.5 rounded tabular-nums ${
      zero ? "text-gray-400" :
      pos  ? "text-green-700 bg-green-50" :
             "text-red-600 bg-red-50"
    }`}>
      {pos ? "+" : ""}{formatItalianNumber(delta, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

const SummaryTable = ({ summary, fmt, notes = {}, canEditNotes = false, onEditNote }) => {
  const { rows, totals } = summary;
  const hasDelta = rows.some(r => r.delta != null);
  const sezioniAttive = [
    { key: "payroll_zucchetti",  label: "Zucchetti" },
    { key: "payroll_iipay",      label: "iiPay" },
    { key: "altri_adempimenti",  label: "Adempimenti" },
    { key: "forfait_consulenza", label: "Forfait" },
    { key: "forfait_f24",        label: "F24" },
    { key: "import_file",        label: "Import" },
    { key: "sconto_cliente",     label: "Sconto cliente" },
  ].filter(s => rows.some(r => Number(r[s.key]) !== 0));

  const hasReferente = rows.some(r => r.referente_nome);
  const hasGruppo    = rows.some(r => r.gruppo_cliente);
  return (
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 bg-gray-50">
        <tr>
          <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">Cliente</th>
          {hasGruppo && (
            <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-500 text-xs">Gruppo</th>
          )}
          {hasReferente && (
            <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-500 text-xs">Referente</th>
          )}
          {sezioniAttive.map(s => (
            <th key={s.key} className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-700 whitespace-nowrap">{s.label}</th>
          ))}
          <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">Totale €</th>
          {hasDelta && (
            <th className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-500 text-xs whitespace-nowrap">Δ mese prec.</th>
          )}
          <th className="text-center px-2 py-2 border-b border-gray-200 font-semibold text-gray-500 text-xs">Bozze</th>
          <th className="text-center px-2 py-2 border-b border-gray-200 text-gray-400 text-xs">Note</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const noteCliente = notes[r.codice_univoco_azienda] || [];
          const hasAlert = noteCliente.some((n) => n.alert);
          return (
            <tr key={r.codice_univoco_azienda} className={`border-b border-gray-100 ${hasAlert ? "bg-red-50" : r.n_bozza > 0 ? "bg-yellow-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
              <td className="px-3 py-1.5 text-gray-800 font-medium">{r.ragione_sociale}</td>
              {hasGruppo && (
                <td className="px-3 py-1.5 text-gray-400 text-xs">{r.gruppo_cliente || "—"}</td>
              )}
              {hasReferente && (
                <td className="px-3 py-1.5 text-gray-500 text-xs">{r.referente_nome || "—"}</td>
              )}
              {sezioniAttive.map(s => (
                <td key={s.key} className="px-3 py-1.5 text-right text-gray-700 tabular-nums">
                  {Number(r[s.key]) !== 0 ? fmt(r[s.key]) : <span className="text-gray-300">—</span>}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right font-semibold text-gray-900 tabular-nums">{fmt(r.totale)}</td>
              {hasDelta && (
                <td className="px-3 py-1.5 text-right"><DeltaBadge delta={r.delta} /></td>
              )}
              <td className="px-2 py-1.5 text-center">
                {r.n_bozza > 0 && (
                  <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-1.5 py-0.5 rounded">{r.n_bozza}</span>
                )}
              </td>
              <td className="px-2 py-1.5 text-center">
                <div className="flex flex-col items-center gap-1">
                  {noteCliente.length > 0 ? noteCliente.map((nota) => (
                    <button
                      key={nota.id}
                      title={nota.nota}
                      onClick={() => canEditNotes && onEditNote?.(r.codice_univoco_azienda, r.ragione_sociale, nota)}
                      className={`max-w-[150px] px-1.5 py-0.5 rounded text-[10px] font-medium truncate ${
                        nota.alert
                          ? "bg-red-100 text-red-700"
                          : nota.tipo === "ADMIN"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-blue-100 text-blue-700"
                      } ${canEditNotes ? "cursor-pointer hover:ring-1 hover:ring-blue-300" : "cursor-default"}`}
                    >
                      {nota.alert ? "! " : ""}{NOTE_TYPE_LABEL[nota.tipo] || "Nota"}{nota.titolo ? `: ${nota.titolo}` : ""}
                    </button>
                  )) : (
                    <span className="text-gray-300">—</span>
                  )}
                  {canEditNotes && (
                    <button
                      title="Aggiungi nota"
                      onClick={() => onEditNote?.(r.codice_univoco_azienda, r.ragione_sociale)}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      {noteCliente.length
                        ? <HiOutlineAnnotation size={16} className="text-blue-500" />
                        : <HiOutlinePencil size={14} className="text-gray-300 hover:text-gray-500" />
                      }
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
          <td className="px-3 py-2 text-gray-800">TOTALE</td>
          {hasGruppo && <td />}
          {hasReferente && <td />}
          {sezioniAttive.map(s => (
            <td key={s.key} className="px-3 py-2 text-right text-gray-800 tabular-nums">{fmt(totals[s.key])}</td>
          ))}
          <td className="px-3 py-2 text-right text-green-700 tabular-nums">{fmt(totals.totale)}</td>
          {hasDelta && (
            <td className="px-3 py-2 text-right"><DeltaBadge delta={totals.delta} /></td>
          )}
          <td className="px-2 py-2 text-center">
            {totals.n_bozza > 0 && (
              <span className="bg-yellow-200 text-yellow-800 text-xs font-bold px-1.5 py-0.5 rounded">{totals.n_bozza}</span>
            )}
          </td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
};

// Tabella trend multi-periodo: righe=clienti, colonne=mesi
const TrendTable = ({ data, fmt }) => {
  const { periodi, clienti } = data;
  if (!clienti.length) return <p className="text-gray-400 text-sm p-4">Nessun dato disponibile per i periodi selezionati.</p>;

  const periodi_con_dati = periodi.filter(p => p.id);

  const cellClass = (val, prevVal) => {
    if (val == null) return "text-gray-300";
    if (prevVal == null) return "text-gray-700";
    if (val > prevVal) return "text-green-700 font-medium";
    if (val < prevVal) return "text-red-600 font-medium";
    return "text-gray-700";
  };

  // Totali per periodo
  const totaliPerPeriodo = periodi_con_dati.map(p =>
    clienti.reduce((s, c) => s + (Number(c.totali[p.id]) || 0), 0)
  );

  return (
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 bg-gray-50">
        <tr>
          <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700 min-w-[180px]">Cliente</th>
          {periodi_con_dati.map(p => (
            <th key={p.id} className="text-right px-3 py-2 border-b border-gray-200 font-semibold text-gray-600 whitespace-nowrap text-xs">
              {String(p.mese).padStart(2,"0")}/{p.anno}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {clienti.map((c, i) => {
          const valori = periodi_con_dati.map(p => c.totali[p.id] != null ? Number(c.totali[p.id]) : null);
          return (
            <tr key={c.codice} className={`border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
              <td className="px-3 py-1.5 text-gray-800 font-medium">{c.ragione_sociale}</td>
              {valori.map((v, j) => (
                <td key={j} className={`px-3 py-1.5 text-right tabular-nums ${cellClass(v, valori[j - 1])}`}>
                  {v != null ? fmt(v) : <span className="text-gray-200">—</span>}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
          <td className="px-3 py-2 text-gray-800">TOTALE</td>
          {totaliPerPeriodo.map((t, j) => (
            <td key={j} className={`px-3 py-2 text-right tabular-nums ${j > 0 && t > totaliPerPeriodo[j-1] ? "text-green-700" : j > 0 && t < totaliPerPeriodo[j-1] ? "text-red-600" : "text-gray-800"}`}>
              {fmt(t)}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
};
